import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import ForceGraph3D from "react-force-graph-3d";
import SpriteText from "three-spritetext";
import * as THREE from "three";
import { Alert, Badge, Button, Form, Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import { getLevelColor } from "../utils/levelColors";
import GalaxyHomeScene from "./GalaxyHomeScene";
import "./Universe.css";

const API_URL = process.env.REACT_APP_API_URL;
const S3_BASE_URL = process.env.REACT_APP_S3_BASE_URL;
const HOME_SERIE_NAMES = ["A", "B", "C"];
const HOME_LEVEL_X_GAP = 2300;
const HOME_LEVEL_Z_GAP = -4200;
const HOME_LEVEL_SUN_OFFSET = { x: -360, y: 420, z: 620 };

const formatNumber = (value, digits = 1) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "0";
  return parsed.toLocaleString("vi-VN", {
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
  });
};

const getTeamLogoUrl = (path) => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  return `${S3_BASE_URL}/${path}`;
};

const getPlayerImageUrl = (path) => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  if (path.startsWith("player/")) return `${S3_BASE_URL}/${path}`;
  return `${S3_BASE_URL}/player/${path}`;
};

const getNodeColor = (node) => {
  const isTeam = node.type === "team";
  return isTeam ? "#38d39f" : node.season_points >= 0 ? "#66b8ff" : "#ffb75e";
};

const getLinkNodeId = (endpoint) => (typeof endpoint === "object" ? endpoint?.id : endpoint);

const getLevelLetter = (level) => {
  const name = String(level?.name || "").toUpperCase();
  const match = name.match(/\b([A-H])\b/);
  return match?.[1] || "";
};

const getHomeMapLevels = (levels = []) => {
  const picked = levels.filter((level) => HOME_SERIE_NAMES.includes(getLevelLetter(level)));
  return picked.length ? picked : levels.slice(0, 2);
};

const getLevelDisplayName = (level) => {
  const letter = getLevelLetter(level);
  return letter ? `Serie ${letter}` : level?.name || "Serie";
};

const seededNoise = (value, salt = 0) => {
  const text = `${value || ""}-${salt}`;
  let hash = 0;
  for (let index = 0; index < text.length; index += 1) {
    hash = (hash * 31 + text.charCodeAt(index)) % 2147483647;
  }
  return ((Math.sin(hash) + 1) / 2) * 2 - 1;
};

const withGalaxyLayout = (graphData) => {
  const nodes = (graphData?.nodes || []).map((node) => ({ ...node }));
  const sourceLinks = (graphData?.links || []).map((link) => ({ ...link }));
  const renderLinks = sourceLinks.filter((link) => link.type === "team-player" || link.type === "upcoming-match");
  const nodeMap = new Map(nodes.map((node) => [node.id, node]));
  const teamNodes = nodes
    .filter((node) => node.type === "team")
    .sort((a, b) => Number(b.strength || 0) - Number(a.strength || 0));
  const playersByTeam = new Map(teamNodes.map((team) => [team.id, []]));
  const assignedPlayers = new Set();

  sourceLinks.forEach((link) => {
    if (link.type !== "team-player") return;
    const source = nodeMap.get(getLinkNodeId(link.source));
    const target = nodeMap.get(getLinkNodeId(link.target));
    const team = source?.type === "team" ? source : target?.type === "team" ? target : null;
    const player = source?.type === "player" ? source : target?.type === "player" ? target : null;
    if (!team || !player) return;
    playersByTeam.get(team.id)?.push(player);
    assignedPlayers.add(player.id);
  });

  const goldenAngle = Math.PI * (3 - Math.sqrt(5));
  teamNodes.forEach((team, index) => {
    const angle = index * goldenAngle;
    const layer = Math.floor(index / 7);
    const radius = index === 0 ? 0 : 180 + Math.sqrt(index) * 126 + layer * 68;
    const x = Math.cos(angle) * radius;
    const y = Math.sin(angle) * radius * 0.86;
    const z =
      Math.sin(angle * 1.18) * 260 +
      Math.cos(index * 0.53) * 120 +
      seededNoise(team.id, index) * 360;
    Object.assign(team, { x, y, z, fx: x, fy: y, fz: z });

    const players = playersByTeam.get(team.id) || [];
    players
      .sort((a, b) => Math.abs(Number(b.season_points || 0)) - Math.abs(Number(a.season_points || 0)))
      .forEach((player, playerIndex) => {
        const playerAngle = (playerIndex / Math.max(1, players.length)) * Math.PI * 2 + index * 0.41;
        const orbit = 62 + (playerIndex % 4) * 18 + Math.floor(playerIndex / 10) * 22;
        const px = x + Math.cos(playerAngle) * orbit;
        const py = y + Math.sin(playerAngle) * orbit * 0.82;
        const pz =
          z +
          Math.sin(playerAngle * 1.3) * 34 +
          Math.cos(playerIndex * 0.7) * 14 +
          seededNoise(player.id, playerIndex) * 28;
        Object.assign(player, { x: px, y: py, z: pz, fx: px, fy: py, fz: pz });
      });
  });

  nodes
    .filter((node) => node.type === "player" && !assignedPlayers.has(node.id))
    .forEach((player, index) => {
      const angle = index * goldenAngle;
      const radius = 720 + Math.sqrt(index) * 44;
      const x = Math.cos(angle) * radius;
      const y = Math.sin(angle) * radius * 0.86;
      const z = Math.sin(angle * 1.1) * 220 + seededNoise(player.id, index) * 260;
      Object.assign(player, { x, y, z, fx: x, fy: y, fz: z });
    });

  return { ...graphData, nodes, links: renderLinks };
};

const createNodeObject = (node, focusNodeIds, selectedNode, focusedLevelNode) => {
  const isTeam = node.type === "team";
  const isLevel = node.type === "level";
  const focusedLevelId = focusedLevelNode ? String(focusedLevelNode.raw_id || focusedLevelNode.graph_level_id || "") : "";
  const isOutsideFocusedLevel =
    !isLevel
      && focusedLevelId
      && String(node.graph_level_id || "") !== focusedLevelId;
  const isDimmed =
    isOutsideFocusedLevel
    || (!isLevel && focusNodeIds?.size > 0 && !focusNodeIds.has(node.id));
  const shouldShowPlayerLabel =
    selectedNode?.type === "team" && node.type === "player" && focusNodeIds?.has(node.id);
  const color = isLevel ? getLevelColor(node.name).solid : getNodeColor(node);
  const radius = isLevel
    ? 46
    : isTeam
      ? 7 + (node.visual_size || 0.5) * 15
      : 3.2 + Math.min(2.8, Math.abs(node.season_points || 0) / 18);
  const group = new THREE.Group();

  const coreGeometry = new THREE.SphereGeometry(radius, 24, 24);
  const coreMaterial = new THREE.MeshLambertMaterial({
    color,
    transparent: isDimmed,
    opacity: isDimmed ? 0.08 : 1,
    emissive: color,
    emissiveIntensity: isDimmed ? 0.015 : isLevel ? 1.15 : isTeam ? 0.62 : 0.36,
  });
  const core = new THREE.Mesh(coreGeometry, coreMaterial);
  group.add(core);

  const glowGeometry = new THREE.SphereGeometry(radius * (isLevel ? 3.4 : isTeam ? 2.4 : 2), 24, 24);
  const glowMaterial = new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity: isDimmed ? 0.004 : isLevel ? 0.16 : isTeam ? 0.12 : 0.08,
    depthWrite: false,
  });
  group.add(new THREE.Mesh(glowGeometry, glowMaterial));

  if (isLevel) {
    const haloGeometry = new THREE.RingGeometry(radius * 1.45, radius * 1.5, 96);
    const haloMaterial = new THREE.MeshBasicMaterial({
      color: getLevelColor(node.name).soft,
      transparent: true,
      opacity: 0.38,
      side: THREE.DoubleSide,
    });
    const halo = new THREE.Mesh(haloGeometry, haloMaterial);
    halo.rotation.x = Math.PI / 2.2;
    group.add(halo);

    const label = new SpriteText(node.name || "");
    label.color = "#ffffff";
    label.textHeight = 12;
    label.backgroundColor = getLevelColor(node.name).text;
    label.padding = 2.4;
    label.position.y = radius + 18;
    group.add(label);
  }

  if (isTeam) {
    const orbitGeometry = new THREE.RingGeometry(radius * 2.8, radius * 2.86, 80);
    const orbitMaterial = new THREE.MeshBasicMaterial({
      color: "#9ef3cf",
      transparent: true,
      opacity: isDimmed ? 0.012 : 0.28,
      side: THREE.DoubleSide,
    });
    const orbit = new THREE.Mesh(orbitGeometry, orbitMaterial);
    orbit.rotation.x = Math.PI / 2.35;
    group.add(orbit);

    const powerLabel = new SpriteText(`Power ${formatNumber(node.strength, 0)}`);
    powerLabel.color = isDimmed ? "#41606a" : "#9ef3cf";
    powerLabel.textHeight = 3.2;
    powerLabel.backgroundColor = "rgba(2,6,23,0.38)";
    powerLabel.padding = 1;
    powerLabel.position.y = -(radius + 7);
    group.add(powerLabel);
  }

  if (isTeam) {
    const label = new SpriteText(node.name || "");
    label.color = isDimmed ? "#475569" : "#f8fafc";
    label.textHeight = 6;
    label.backgroundColor = "rgba(2,6,23,0.35)";
    label.padding = 2;
    label.position.y = radius + 8;
    group.add(label);
  }

  if (shouldShowPlayerLabel) {
    const label = new SpriteText(node.name || "");
    label.color = "#f8fafc";
    label.textHeight = 3.6;
    label.backgroundColor = "rgba(2,6,23,0.62)";
    label.padding = 1.2;
    label.position.y = radius + 5.6;
    group.add(label);
  }

  return group;
};

const normalizeGraphVisuals = (graphData) => {
  const nodes = graphData?.nodes || [];
  const teamStrengths = nodes
    .filter((node) => node.type === "team")
    .map((node) => Number(node.strength))
    .filter(Number.isFinite);
  const minStrength = Math.min(...teamStrengths);
  const maxStrength = Math.max(...teamStrengths);
  const spread = maxStrength - minStrength || 1;

  return withGalaxyLayout({
    ...graphData,
    nodes: nodes.map((node) => {
      if (node.type !== "team") return node;
      const normalized = (Number(node.strength || 0) - minStrength) / spread;
      return {
        ...node,
        visual_size: 0.18 + normalized * 0.82,
      };
    }),
  });
};

const withLevelMetadata = (graphData, level, index) => ({
  ...graphData,
  nodes: (graphData.nodes || []).map((node) => ({
    ...node,
    graph_level_id: String(level?.id || ""),
    graph_level_name: level?.name || "",
    x: (node.x || 0) + index * HOME_LEVEL_X_GAP,
    y: node.y || 0,
    z: (node.z || 0) + index * HOME_LEVEL_Z_GAP,
    fx: Number.isFinite(node.fx) ? node.fx + index * HOME_LEVEL_X_GAP : node.fx,
    fy: node.fy,
    fz: Number.isFinite(node.fz) ? node.fz + index * HOME_LEVEL_Z_GAP : node.fz,
  })),
  links: (graphData.links || []).map((link) => ({
    ...link,
    graph_level_id: String(level?.id || ""),
    graph_level_name: level?.name || "",
  })),
});

const combineHomeGraphs = (items, levels) => {
  const validItems = (items || []).filter((item) => item?.level?.id);
  const normalized = validItems.map((item, index) =>
    withLevelMetadata(normalizeGraphVisuals(item.data || { nodes: [], links: [], levels: [] }), item.level, index),
  );
  const levelNodes = normalized.map((item, index) => {
    const level = validItems[index]?.level;
    const positionedNodes = (item.nodes || []).filter(
      (node) => node.type !== "level" && Number.isFinite(node.x) && Number.isFinite(node.y) && Number.isFinite(node.z),
    );
    const center = positionedNodes.length
      ? positionedNodes.reduce(
        (next, node) => ({
          x: next.x + node.x / positionedNodes.length,
          y: next.y + node.y / positionedNodes.length,
          z: next.z + node.z / positionedNodes.length,
        }),
        { x: 0, y: 0, z: 0 },
      )
      : { x: index * HOME_LEVEL_X_GAP, y: 0, z: index * HOME_LEVEL_Z_GAP };

    return {
      id: `level-${level.id}`,
      raw_id: level.id,
      type: "level",
      name: getLevelDisplayName(level),
      graph_level_id: String(level.id),
      graph_level_name: level.name,
      x: center.x + HOME_LEVEL_SUN_OFFSET.x,
      y: center.y + HOME_LEVEL_SUN_OFFSET.y,
      z: center.z + HOME_LEVEL_SUN_OFFSET.z,
      fx: center.x + HOME_LEVEL_SUN_OFFSET.x,
      fy: center.y + HOME_LEVEL_SUN_OFFSET.y,
      fz: center.z + HOME_LEVEL_SUN_OFFSET.z,
    };
  });

  return {
    season_id: normalized[0]?.season_id,
    season_name: normalized[0]?.season_name,
    levels,
    nodes: [...levelNodes, ...normalized.flatMap((item) => item.nodes || [])],
    links: normalized.flatMap((item) => item.links || []),
  };
};

const LoadingBlock = ({ label }) => (
  <div className="universe-loading">
    <Spinner animation="border" />
    <span>{label}</span>
  </div>
);

const UniverseGuide = () => (
  <div className="universe-map-guide" aria-label="Cách đọc bản đồ Universe">
    <strong>Cách đọc bản đồ</strong>
    <span><i className="legend-team" /> Chấm xanh là đội; kích thước + Power thể hiện sức mạnh.</span>
    <span><i className="legend-up" /> Sao xanh là VĐV đang cộng điểm mùa.</span>
    <span><i className="legend-down" /> Sao cam là VĐV đang âm điểm mùa.</span>
    <span><i className="legend-rival" /> Đường mờ nối VĐV với đội.</span>
    <span><i className="legend-upcoming" /> Đường sáng là trận vòng tới.</span>
  </div>
);

const homeNavItems = [
  { to: "/dashboard", label: "Tổng Quan" },
  { to: "/league-details", label: "BXH" },
  { to: "/match-results", label: "Lịch" },
  { to: "/players", label: "VĐV" },
  { to: "/teams", label: "Đội" },
  { to: "/tournaments", label: "Giải ĐĐ" },
  { to: "/universe", label: "Dự đoán" },
  { to: "/news", label: "Tin Tức" },
];

const HomeConstellationNav = () => (
  <header className="universe-home-nav universe-home-nav-v2" aria-label="Điều hướng chính">
    <Link to="/" className="universe-home-brand">
      <img src="https://s3.cloudfly.vn/player-images/logo/logo.png" alt="HDSPL" />
      <span>
        <strong>HDSPL</strong>
        <em>Universe</em>
      </span>
    </Link>
    <nav className="universe-home-nav-pills">
      {homeNavItems.map((item) => (
        <Link to={item.to} key={item.to} className="universe-home-nav-pill">
          {item.label}
        </Link>
      ))}
    </nav>
  </header>
);

const NodeDetails = ({ node, onFocusNode, onClose }) => {
  if (!node) {
    return (
      <div className="universe-empty">
        <strong>Chọn một hệ sao hoặc VĐV</strong>
        <span>Click lõi xanh để xem sức mạnh đội. Click sao nhỏ để xem VĐV đang kéo đội lên hay làm đội mất điểm.</span>
      </div>
    );
  }

  const isTeam = node.type === "team";
  const imageUrl = isTeam ? getTeamLogoUrl(node.logo) : getPlayerImageUrl(node.image);
  const winRate = node.matches > 0 ? Math.round((node.wins / node.matches) * 100) : 0;
  const playerTrend =
    node.season_points > 0
      ? "Đang tạo lực kéo dương cho đội trong mùa này."
      : node.season_points < 0
        ? "Đang là điểm suy giảm cần theo dõi trong mùa này."
        : "Chưa tạo biến động điểm rõ ràng trong mùa này.";
  const playerRole =
    node.matches >= 8
      ? "Trụ cột thi đấu nhiều"
      : node.matches > 0
        ? "VĐV đã có dữ liệu thi đấu"
        : "VĐV chưa có nhiều dữ liệu mùa này";
  const teamRole =
    node.strength >= 25
      ? "Hệ sao mạnh trong serie"
      : node.strength >= 5
        ? "Hệ sao cân bằng"
        : "Hệ sao đang yếu thế";

  return (
    <div className="universe-detail">
      {onClose && (
        <button type="button" className="universe-detail-close" onClick={onClose} aria-label="Đóng thông tin">
          ×
        </button>
      )}
      <div className="universe-detail-head">
        {imageUrl ? <img src={imageUrl} alt="" /> : <span>{node.name?.charAt(0) || "?"}</span>}
        <div>
          <Badge className="universe-soft-badge">{isTeam ? teamRole : playerRole}</Badge>
          <h2>{node.name}</h2>
        </div>
      </div>

      {isTeam ? (
        <>
          <p className="universe-insight">
            Đây là lõi của một hệ sao. VĐV trong đội xoay quanh lõi này; kích thước và độ sáng phản ánh sức mạnh tổng hợp của đội trong mùa.
          </p>
          <div className="universe-metric-grid">
            <span>
              Điểm
              <strong>{formatNumber(node.points)}</strong>
            </span>
            <span>
              Thắng / thua
              <strong>{node.wins || 0}-{node.losses || 0}</strong>
            </span>
            <span>
              Thành viên
              <strong>{node.player_count}</strong>
            </span>
          </div>
          <div className="universe-metric-grid universe-metric-grid-secondary">
            <span>
              Số trận
              <strong>{node.matches || 0}</strong>
            </span>
            <span>
              Win-rate
              <strong>{Math.round((node.win_rate || 0) * 100)}%</strong>
            </span>
            <span>
              Sức mạnh
              <strong>{formatNumber(node.strength)}</strong>
            </span>
          </div>
          <Button as={Link} to={`/teams/${node.raw_id}`} className="universe-detail-link">
            Mở hồ sơ đội
          </Button>
          <Button type="button" onClick={onFocusNode} className="universe-focus-link">
            Zoom vào hệ sao
          </Button>
        </>
      ) : (
        <>
          <p className="universe-insight">{playerTrend}</p>
          <div className="universe-metric-grid">
            <span>
              Điểm trình
              <strong>{formatNumber(node.level_point)}</strong>
            </span>
            <span>
              Điểm mùa
              <strong>{formatNumber(node.season_points)}</strong>
            </span>
            <span>
              Win-rate
              <strong>{node.matches > 0 ? `${winRate}%` : "Chưa có"}</strong>
            </span>
          </div>
          <div className="universe-meaning-list">
            <span>
              <strong>Vai trò trên bản đồ</strong>
              Sao nhỏ quanh {node.team_name || "đội"}. Càng sáng nghĩa là dữ liệu mùa càng đáng chú ý.
            </span>
            <span>
              <strong>Tác động thi đấu</strong>
              {node.matches > 0 ? `${node.wins || 0}/${node.matches || 0} trận thắng được ghi nhận.` : "Chưa đủ trận để đánh giá phong độ."}
            </span>
          </div>
          {node.team_raw_id && (
            <Button as={Link} to={`/teams/${node.team_raw_id}`} className="universe-focus-link">
              Mở đội {node.team_name || ""}
            </Button>
          )}
          <Button as={Link} to={`/players/${node.raw_id}`} className="universe-detail-link">
            Mở hồ sơ VĐV
          </Button>
          <Button type="button" onClick={onFocusNode} className="universe-focus-link">
            Zoom vào VĐV
          </Button>
        </>
      )}
    </div>
  );
};

const TeamOption = ({ team }) => `${team.name} (${formatNumber(team.strength)})`;

const PredictionPanel = ({ seasonId, teams }) => {
  const [homeTeamId, setHomeTeamId] = useState("");
  const [guestTeamId, setGuestTeamId] = useState("");
  const [prediction, setPrediction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setPrediction(null);
    setError("");
    setHomeTeamId(teams[0]?.id ? String(teams[0].id) : "");
    setGuestTeamId(teams[1]?.id ? String(teams[1].id) : "");
  }, [teams]);

  const predict = async () => {
    if (!homeTeamId || !guestTeamId || homeTeamId === guestTeamId) {
      setError("Chọn hai đội khác nhau.");
      return;
    }

    try {
      setLoading(true);
      setError("");
      const res = await axios.get(`${API_URL}/universe/predict`, {
        params: {
          season_id: seasonId,
          home_team_id: homeTeamId,
          guest_team_id: guestTeamId,
        },
      });
      setPrediction(res.data);
    } catch (err) {
      setPrediction(null);
      setError(err.response?.data?.error || "Không thể dự đoán trận này.");
    } finally {
      setLoading(false);
    }
  };

  const renderPlayers = (players = []) => (
    <div className="universe-key-player-list">
      {players.slice(0, 3).map((player) => (
        <span key={player.id}>
          <strong>{player.name}</strong>
          <small>
            {formatNumber(player.season_points)} điểm mùa · {player.wins}/{player.matches} thắng
          </small>
        </span>
      ))}
    </div>
  );

  return (
    <div className="universe-simulator">
      <div className="universe-panel-title">
        <span>What-if Simulator</span>
        <h2>Dự đoán trận</h2>
      </div>

      <div className="universe-select-grid">
        <Form.Group>
          <Form.Label>Đội 1</Form.Label>
          <Form.Select value={homeTeamId} onChange={(event) => setHomeTeamId(event.target.value)}>
            {teams.map((team) => (
              <option value={team.id} key={team.id}>
                {TeamOption({ team })}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        <Form.Group>
          <Form.Label>Đội 2</Form.Label>
          <Form.Select value={guestTeamId} onChange={(event) => setGuestTeamId(event.target.value)}>
            {teams.map((team) => (
              <option value={team.id} key={team.id}>
                {TeamOption({ team })}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </div>

      <Button className="client-primary-btn universe-predict-btn" onClick={predict} disabled={loading || teams.length < 2}>
        {loading ? "Đang tính..." : "Dự đoán"}
      </Button>

      {error && (
        <Alert variant="warning" className="universe-alert">
          {error}
        </Alert>
      )}

      {prediction && (
        <div className="universe-prediction">
          <div className="universe-probability-row">
            <span>
              <strong>{prediction.home_team.name}</strong>
              <b>{prediction.home_win_probability}%</b>
            </span>
            <span>
              <strong>{prediction.guest_team.name}</strong>
              <b>{prediction.guest_win_probability}%</b>
            </span>
          </div>
          <div className="universe-probability-track">
            <div style={{ width: `${prediction.home_win_probability}%` }} />
          </div>
          <div className="universe-confidence">
            Độ tin cậy {prediction.confidence}% · Đối đầu {prediction.head_to_head.matches} trận
          </div>

          <div className="universe-breakdown">
            <span>
              <small>Sức mạnh</small>
              <strong>
                {formatNumber(prediction.home_team.strength)} / {formatNumber(prediction.guest_team.strength)}
              </strong>
            </span>
            <span>
              <small>H2H modifier</small>
              <strong>{formatNumber(prediction.strength_breakdown.h2h_modifier)}</strong>
            </span>
          </div>

          <div className="universe-key-players">
            <h3>VĐV tạo khác biệt</h3>
            <div>
              {renderPlayers(prediction.key_players.home)}
              {renderPlayers(prediction.key_players.guest)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Universe = ({ mode = "full" }) => {
  const isHomeMode = mode === "home";
  const graphRef = useRef(null);
  const galaxyRef = useRef(null);
  const graphDataRef = useRef({ nodes: [], links: [], levels: [] });
  const sizeRef = useRef({ width: 820, height: 620 });
  const manualFocusTimerRef = useRef(null);
  const homeUserFocusedRef = useRef(false);
  const wrapRef = useRef(null);
  const [seasons, setSeasons] = useState([]);
  const [selectedSeasonId, setSelectedSeasonId] = useState("");
  const [selectedLevelId, setSelectedLevelId] = useState("");
  const [graph, setGraph] = useState({ nodes: [], links: [], levels: [] });
  const [teams, setTeams] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [focusedTeamNode, setFocusedTeamNode] = useState(null);
  const [focusedLevelNode, setFocusedLevelNode] = useState(null);
  const [teamSearchTerm, setTeamSearchTerm] = useState("");
  const [homeToolsOpen, setHomeToolsOpen] = useState(false);
  const [showScheduleLinks, setShowScheduleLinks] = useState(false);
  const [homeHintVisible, setHomeHintVisible] = useState(true);
  const [loadingSeasons, setLoadingSeasons] = useState(true);
  const [loadingGraph, setLoadingGraph] = useState(false);
  const [error, setError] = useState("");
  const [size, setSize] = useState({ width: 820, height: 620 });
  const hasGraphData = graph.nodes.length > 0;

  useEffect(() => {
    graphDataRef.current = graph;
  }, [graph]);

  useEffect(() => {
    sizeRef.current = size;
  }, [size]);

  useEffect(() => () => {
    if (manualFocusTimerRef.current) window.clearTimeout(manualFocusTimerRef.current);
  }, []);

  const getUniverseErrorMessage = (fallback, err) => {
    const status = err?.response?.status;
    const apiMessage = err?.response?.data?.error || err?.message;
    if (status === 404) {
      return "Không tìm thấy API Universe trên server hiện tại. Kiểm tra lại backend đã deploy route /api/universe chưa.";
    }
    if (status >= 500) {
      return `API Universe đang lỗi server${apiMessage ? `: ${apiMessage}` : "."}`;
    }
    if (apiMessage) {
      return `${fallback} (${apiMessage})`;
    }
    return fallback;
  };

  useEffect(() => {
    let active = true;
    const fetchSeasons = async () => {
      try {
        setLoadingSeasons(true);
        const res = await axios.get(`${API_URL}/universe/seasons`);
        if (!active) return;
        const nextSeasons = res.data || [];
        const firstSeason = nextSeasons[0];
        const firstLevels = firstSeason?.levels || [];
        const initialLevels = isHomeMode ? getHomeMapLevels(firstLevels) : firstLevels;
        setSeasons(nextSeasons);
        setSelectedSeasonId(firstSeason?.id ? String(firstSeason.id) : "");
        setSelectedLevelId(
          isHomeMode
            ? ""
            : initialLevels[0]?.id
              ? String(initialLevels[0].id)
              : "",
        );
      } catch (err) {
        if (active) setError(getUniverseErrorMessage("Không thể tải dữ liệu Universe.", err));
      } finally {
        if (active) setLoadingSeasons(false);
      }
    };
    fetchSeasons();
    return () => {
      active = false;
    };
  }, [isHomeMode]);

  useEffect(() => {
    if (!wrapRef.current) return undefined;

    const resize = () => {
      const rect = wrapRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight || 720;
      const graphHeight = isHomeMode
        ? Math.max(560, viewportHeight)
        : Math.max(480, Math.min(720, Math.floor(viewportHeight * 0.72)));
      setSize({
        width: Math.max(320, Math.floor(rect.width)),
        height: graphHeight,
      });
    };

    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(wrapRef.current);
    window.addEventListener("resize", resize);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", resize);
    };
  }, [isHomeMode, loadingSeasons]);

  const selectedSeason = useMemo(
    () => seasons.find((season) => String(season.id) === String(selectedSeasonId)),
    [seasons, selectedSeasonId],
  );
  const levels = useMemo(
    () => selectedSeason?.levels || graph.levels || [],
    [graph.levels, selectedSeason],
  );
  const homeMapLevels = useMemo(() => (isHomeMode ? getHomeMapLevels(levels) : []), [isHomeMode, levels]);

  const focusNodeIds = useMemo(() => {
    const focusNode = focusedTeamNode;
    if (!focusNode?.id || focusNode.type === "level") return new Set();

    const ids = new Set([focusNode.id]);
    if (focusNode.type === "player" && focusNode.team_raw_id) {
      ids.add(`team-${focusNode.team_raw_id}`);
    }

    const teamId = focusNode.type === "team" ? focusNode.id : focusNode.team_raw_id ? `team-${focusNode.team_raw_id}` : null;
    if (teamId) {
      ids.add(teamId);
      graph.links.forEach((link) => {
        if (link.type !== "team-player") return;
        const sourceId = getLinkNodeId(link.source);
        const targetId = getLinkNodeId(link.target);
        if (sourceId === teamId) ids.add(targetId);
        if (targetId === teamId) ids.add(sourceId);
      });
    }

    return ids;
  }, [focusedTeamNode, graph.links]);

  const isFocusActive = focusNodeIds.size > 0;
  const focusedLevelId = focusedLevelNode ? String(focusedLevelNode.raw_id || focusedLevelNode.graph_level_id || "") : "";
  const isFocusedLink = useCallback(
    (link) => {
      if (focusedLevelId) return String(link.graph_level_id || "") === focusedLevelId;
      if (!isFocusActive) return true;
      const sourceId = getLinkNodeId(link.source);
      const targetId = getLinkNodeId(link.target);
      return focusNodeIds.has(sourceId) && focusNodeIds.has(targetId);
    },
    [focusNodeIds, focusedLevelId, isFocusActive],
  );

  const renderNodeObject = useCallback(
    (node) => createNodeObject(node, focusNodeIds, focusedTeamNode, focusedLevelNode),
    [focusNodeIds, focusedLevelNode, focusedTeamNode],
  );

  const tuneControls = useCallback(() => {
    const controls = graphRef.current?.controls?.();
    if (!controls) return;
    const isMobile = window.innerWidth < 768;
    controls.autoRotate = false;
    controls.enableDamping = true;
    controls.dampingFactor = isMobile ? 0.08 : 0.12;
    controls.rotateSpeed = isMobile ? 0.34 : 0.12;
    controls.zoomSpeed = isMobile ? 0.44 : 0.24;
    controls.panSpeed = isMobile ? 0.36 : 0.22;
    controls.minDistance = 20;
    controls.maxDistance = 2400;
  }, []);

  const getFocusTarget = () =>
    (selectedNode || focusedTeamNode)
      ? { x: (selectedNode || focusedTeamNode).x || 0, y: (selectedNode || focusedTeamNode).y || 0, z: (selectedNode || focusedTeamNode).z || 0 }
      : { x: 0, y: 0, z: 0 };

  const getBoundsForNodes = (nodes) => {
    const positionedNodes = (nodes || []).filter(
      (node) => Number.isFinite(node.x) && Number.isFinite(node.y) && Number.isFinite(node.z),
    );
    if (!positionedNodes.length) return null;

    return positionedNodes.reduce(
      (next, node) => ({
        minX: Math.min(next.minX, node.x),
        maxX: Math.max(next.maxX, node.x),
        minY: Math.min(next.minY, node.y),
        maxY: Math.max(next.maxY, node.y),
        minZ: Math.min(next.minZ, node.z),
        maxZ: Math.max(next.maxZ, node.z),
      }),
      {
        minX: Infinity,
        maxX: -Infinity,
        minY: Infinity,
        maxY: -Infinity,
        minZ: Infinity,
        maxZ: -Infinity,
      },
    );
  };

  const getTeamOverviewNodes = useCallback((teamNode) => {
    if (!teamNode) return [];
    const graphData = graphDataRef.current || { nodes: [], links: [] };
    const nodeById = new Map((graphData.nodes || []).map((node) => [node.id, node]));
    const overviewNodes = new Map([[teamNode.id, teamNode]]);

    (graphData.links || []).forEach((link) => {
      if (link.type !== "team-player") return;
      const sourceId = getLinkNodeId(link.source);
      const targetId = getLinkNodeId(link.target);
      if (sourceId === teamNode.id && nodeById.has(targetId)) overviewNodes.set(targetId, nodeById.get(targetId));
      if (targetId === teamNode.id && nodeById.has(sourceId)) overviewNodes.set(sourceId, nodeById.get(sourceId));
    });

    return Array.from(overviewNodes.values());
  }, []);

  const findTeamForNode = useCallback((node) => {
    if (!node) return null;
    if (node.type === "team") return node;
    if (!node.team_raw_id) return null;
    return (graphDataRef.current?.nodes || []).find((item) => item.id === `team-${node.team_raw_id}`) || null;
  }, []);

  const focusTeamOverview = useCallback((teamNode, duration = 2200) => {
    if (!teamNode) return duration;
    if (isHomeMode && galaxyRef.current) {
      const travel = Math.min(duration, 520);
      galaxyRef.current.focusNode(teamNode, travel);
      return travel;
    }

    const graphInstance = graphRef.current;
    const camera = graphInstance?.camera?.();
    if (!graphInstance) return duration;

    const overviewNodes = getTeamOverviewNodes(teamNode);
    const bounds = getBoundsForNodes(overviewNodes.length ? overviewNodes : [teamNode]);
    if (!bounds) return duration;

    tuneControls();
    const controls = graphInstance.controls?.();
    if (controls) controls.autoRotate = false;

    const center = {
      x: (bounds.minX + bounds.maxX) / 2,
      y: (bounds.minY + bounds.maxY) / 2,
      z: (bounds.minZ + bounds.maxZ) / 2,
    };
    const spanX = Math.max(90, bounds.maxX - bounds.minX);
    const spanY = Math.max(90, bounds.maxY - bounds.minY);
    const spanZ = Math.max(70, bounds.maxZ - bounds.minZ);
    const currentSize = sizeRef.current;
    const aspect = currentSize.width / Math.max(1, currentSize.height);
    const fov = (((camera?.fov || 60) * Math.PI) / 180);
    const distanceForHeight = (spanY * 1.7) / (2 * Math.tan(fov / 2));
    const distanceForWidth = (spanX * 1.55) / (2 * Math.tan(fov / 2) * Math.max(0.65, aspect));
    const focusDistance = Math.max(240, distanceForHeight, distanceForWidth, spanZ * 2.7);
    const currentPosition = camera?.position || {
      x: center.x + focusDistance * 0.32,
      y: center.y + focusDistance * 0.2,
      z: center.z + focusDistance,
    };
    const direction = new THREE.Vector3(
      currentPosition.x - center.x,
      currentPosition.y - center.y,
      currentPosition.z - center.z,
    );
    if (direction.lengthSq() < 1) {
      direction.set(0.32, 0.2, 1);
    }
    direction.normalize();
    const destination = {
      x: center.x + direction.x * focusDistance,
      y: center.y + direction.y * focusDistance + 18,
      z: center.z + direction.z * focusDistance,
    };
    const travelDistance = camera?.position?.distanceTo
      ? camera.position.distanceTo(new THREE.Vector3(destination.x, destination.y, destination.z))
      : focusDistance;
    const travelDuration = Math.max(duration, Math.min(5200, travelDistance * 0.62));

    graphInstance.cameraPosition?.(destination, center, travelDuration);
    return travelDuration;
  }, [getTeamOverviewNodes, isHomeMode, tuneControls]);

  const fitGalaxy = useCallback((duration = 700, options = {}) => {
    const force = Boolean(options.force);
    if (isHomeMode && galaxyRef.current) {
      // Tranh resize/mobile chrome goi fitAll de mat zoom doi dang chon.
      if (!force && homeUserFocusedRef.current) return;
      if (force) homeUserFocusedRef.current = false;
      galaxyRef.current.fitAll(duration);
      galaxyRef.current.setAutoRotate(true);
      return;
    }

    const graphInstance = graphRef.current;
    if (!graphInstance) return;

    tuneControls();
    graphInstance.zoomToFit?.(duration, 110);
  }, [isHomeMode, tuneControls]);

  // Thoat focus he sao: bo dim + (tuy chon) bay ve overview. Xem het/Reset can force fit.
  const clearHomeTeamFocus = useCallback((options = {}) => {
    if (!isHomeMode) return;
    const shouldFit = options.fit !== false;
    const duration = options.duration ?? 700;
    setSelectedNode(null);
    setFocusedTeamNode(null);
    setFocusedLevelNode(null);
    homeUserFocusedRef.current = false;
    galaxyRef.current?.clearFocus?.();
    if (shouldFit) {
      fitGalaxy(duration, { force: true });
    } else {
      galaxyRef.current?.setAutoRotate?.(true);
    }
  }, [fitGalaxy, isHomeMode]);

  const focusLevel = useCallback((levelId, duration = 760) => {
    if (!levelId) return;
    if (isHomeMode && galaxyRef.current) {
      // Roi focus doi truoc khi nhay serie, neu khong dim van giu he sao cu.
      setSelectedNode(null);
      setFocusedTeamNode(null);
      homeUserFocusedRef.current = false;
      galaxyRef.current.clearFocus?.();
      galaxyRef.current.focusSerie(String(levelId), Math.max(1400, duration));
      return;
    }

    const graphInstance = graphRef.current;
    if (!graphInstance) return;

    const levelNodes = (graphDataRef.current?.nodes || []).filter(
      (node) => node.type !== "level"
        && String(node.graph_level_id) === String(levelId)
        && Number.isFinite(node.x)
        && Number.isFinite(node.y)
        && Number.isFinite(node.z),
    );
    if (!levelNodes.length) return;

    tuneControls();
    const bounds = levelNodes.reduce(
      (next, node) => ({
        minX: Math.min(next.minX, node.x),
        maxX: Math.max(next.maxX, node.x),
        minY: Math.min(next.minY, node.y),
        maxY: Math.max(next.maxY, node.y),
        minZ: Math.min(next.minZ, node.z),
        maxZ: Math.max(next.maxZ, node.z),
      }),
      {
        minX: Infinity,
        maxX: -Infinity,
        minY: Infinity,
        maxY: -Infinity,
        minZ: Infinity,
        maxZ: -Infinity,
      },
    );
    const center = {
      x: (bounds.minX + bounds.maxX) / 2,
      y: (bounds.minY + bounds.maxY) / 2,
      z: (bounds.minZ + bounds.maxZ) / 2,
    };
    const spanX = bounds.maxX - bounds.minX;
    const spanY = bounds.maxY - bounds.minY;
    const distance = Math.max(720, spanX * 0.92, spanY * 1.5);
    const controls = graphInstance.controls?.();
    if (controls) controls.autoRotate = false;
    graphInstance.cameraPosition?.(
      { x: center.x + distance * 0.32, y: center.y + distance * 0.2, z: center.z + distance },
      center,
      Math.max(1400, duration),
    );
  }, [isHomeMode, tuneControls]);

  useEffect(() => {
    if (!isHomeMode || !hasGraphData || loadingGraph) return undefined;
    if (homeUserFocusedRef.current) return undefined;
    const firstFit = window.setTimeout(() => fitGalaxy(450), 90);
    const finalFit = window.setTimeout(() => fitGalaxy(450), 420);
    return () => {
      window.clearTimeout(firstFit);
      window.clearTimeout(finalFit);
    };
    // Chi fit khi load graph, khong fit lai theo resize (mobile se mat zoom).
  }, [fitGalaxy, hasGraphData, isHomeMode, loadingGraph, graph.season_id, graph.nodes.length]);

  const zoomCamera = (factor) => {
    const graphInstance = graphRef.current;
    const camera = graphInstance?.camera?.();
    if (!graphInstance || !camera) return;

    tuneControls();
    const target = getFocusTarget();
    graphInstance.cameraPosition(
      {
        x: target.x + (camera.position.x - target.x) * factor,
        y: target.y + (camera.position.y - target.y) * factor,
        z: target.z + (camera.position.z - target.z) * factor,
      },
      target,
      360,
    );
  };

  const focusNode = (node = selectedNode) => {
    if (!node) return;

    if (node.type === "level") {
      setSelectedNode(null);
      setFocusedTeamNode(null);
      setFocusedLevelNode(node);
      if (!isHomeMode) {
        setSelectedLevelId(String(node.raw_id || node.graph_level_id));
      }
      focusLevel(node.raw_id || node.graph_level_id, isHomeMode ? 700 : 2400);
      return;
    }

    if (isHomeMode && galaxyRef.current) {
      galaxyRef.current.focusNode(node, node.type === "player" ? 420 : 520);
      return;
    }

    const teamNode = findTeamForNode(node);
    if (teamNode && node.type === "player") {
      focusTeamOverview(teamNode, 2300);
      return;
    }

    if (!graphRef.current) return;
    tuneControls();
    const graphInstance = graphRef.current;
    const controls = graphInstance.controls?.();
    if (controls) controls.autoRotate = false;
    const camera = graphInstance.camera?.();
    const target = { x: node.x || 0, y: node.y || 0, z: node.z || 0 };
    const focusDistance = node.type === "team" ? 132 : 86;
    const currentPosition = camera?.position || {
      x: target.x + 260,
      y: target.y + 140,
      z: target.z + 520,
    };
    const direction = new THREE.Vector3(
      currentPosition.x - target.x,
      currentPosition.y - target.y,
      currentPosition.z - target.z,
    );
    if (direction.lengthSq() < 1) {
      direction.set(0.42, 0.24, 1);
    }
    direction.normalize();
    const destination = {
      x: target.x + direction.x * focusDistance,
      y: target.y + direction.y * focusDistance + (node.type === "team" ? 14 : 8),
      z: target.z + direction.z * focusDistance,
    };
    const travelDistance = camera?.position?.distanceTo
      ? camera.position.distanceTo(new THREE.Vector3(destination.x, destination.y, destination.z))
      : focusDistance;
    const duration = Math.max(1650, Math.min(4200, travelDistance * 0.7));

    graphInstance.cameraPosition(destination, target, duration);
  };

  const selectNode = (node) => {
    if (!node) return;
    if (manualFocusTimerRef.current) {
      window.clearTimeout(manualFocusTimerRef.current);
      manualFocusTimerRef.current = null;
    }

    if (node.type === "level") {
      focusNode(node);
      return;
    }

    if (isHomeMode) {
      setFocusedLevelNode(null);
      homeUserFocusedRef.current = true;
      if (node.type === "team") {
        // Tap doi = chi zoom he sao (giu camera). Khong mo panel de dong panel khong mat zoom.
        setFocusedTeamNode(node);
        setSelectedNode(null);
        const runZoom = () => galaxyRef.current?.focusTeamSystem?.(node, 650);
        runZoom();
        requestAnimationFrame(runZoom);
        window.setTimeout(runZoom, 40);
        window.setTimeout(runZoom, 180);
      } else {
        const teamNode = findTeamForNode(node);
        setFocusedTeamNode(teamNode || null);
        setSelectedNode(node);
        const runZoom = () => {
          if (teamNode) galaxyRef.current?.focusTeamSystem?.(teamNode, 650);
          else galaxyRef.current?.focusNode?.(node, 480);
        };
        runZoom();
        requestAnimationFrame(runZoom);
        window.setTimeout(runZoom, 40);
      }
      return;
    }

    focusNode(node);
    manualFocusTimerRef.current = window.setTimeout(() => {
      setSelectedNode(node);
      manualFocusTimerRef.current = null;
    }, 120);
  };

  const searchTeam = (value) => {
    setTeamSearchTerm(value);
    const keyword = value.trim().toLowerCase();
    if (keyword.length < 2) return;

    const teamNode = graphDataRef.current.nodes.find(
      (node) => node.type === "team" && String(node.name || "").toLowerCase().includes(keyword),
    );
    if (!teamNode) return;

    selectNode(teamNode);
  };

  useEffect(() => {
    if (!selectedSeasonId) return;
    const nextLevels = isHomeMode ? homeMapLevels : levels;
    const firstLevelId = nextLevels[0]?.id;
    if (!firstLevelId) return;
    if (isHomeMode && selectedLevelId === "") return;
    if (!nextLevels.some((level) => String(level.id) === String(selectedLevelId))) {
      setSelectedLevelId(String(firstLevelId));
    }
  }, [homeMapLevels, isHomeMode, levels, selectedSeasonId, selectedLevelId]);

  useEffect(() => {
    if (!selectedSeasonId || (!isHomeMode && !selectedLevelId) || (isHomeMode && homeMapLevels.length === 0)) return;
    let active = true;

    const fetchGraph = async () => {
      try {
        setLoadingGraph(true);
        setError("");
        setSelectedNode(null);
        setFocusedTeamNode(null);
        setFocusedLevelNode(null);
        const loadLevels = (isHomeMode ? homeMapLevels : levels.filter((level) => String(level?.id) === String(selectedLevelId)))
          .filter((level) => level?.id);
        const teamsRequests = loadLevels.map((level) =>
          axios.get(`${API_URL}/universe/teams`, {
            params: { season_id: selectedSeasonId, level_team_id: level.id },
          }),
        );
        const graphRequests = isHomeMode
          ? loadLevels.map((level) =>
            axios.get(`${API_URL}/universe/graph`, {
              params: { season_id: selectedSeasonId, level_team_id: level.id },
            }).then((res) => ({ level, data: res.data })),
          )
          : [Promise.resolve({ level: loadLevels[0], data: { nodes: [], links: [], levels: [] } })];
        const [graphSettled, teamsSettled] = await Promise.all([
          Promise.allSettled(graphRequests),
          Promise.allSettled(teamsRequests),
        ]);
        if (!active) return;
        const graphResults = graphSettled
          .filter((result) => result.status === "fulfilled")
          .map((result) => result.value);
        const teamsResults = teamsSettled
          .filter((result) => result.status === "fulfilled")
          .map((result) => result.value);
        if (isHomeMode && graphResults.length === 0) {
          const rejected = graphSettled.find((result) => result.status === "rejected");
          throw rejected?.reason || new Error("Không có dữ liệu graph.");
        }
        if (teamsResults.length === 0) {
          const rejected = teamsSettled.find((result) => result.status === "rejected");
          throw rejected?.reason || new Error("Không có dữ liệu đội.");
        }
        setGraph(isHomeMode ? combineHomeGraphs(graphResults, homeMapLevels) : { nodes: [], links: [], levels: [] });
        setTeams(teamsResults.flatMap((res) => res.data || []));
        setTeamSearchTerm("");
        setHomeToolsOpen(false);
        setShowScheduleLinks(false);
        setTimeout(() => {
          if (!isHomeMode) return;
          homeUserFocusedRef.current = false;
          fitGalaxy(900, { force: true });
          galaxyRef.current?.setAutoRotate?.(true);
        }, 120);
      } catch (err) {
        if (active) setError(getUniverseErrorMessage("Không thể dựng bản đồ Universe.", err));
      } finally {
        if (active) setLoadingGraph(false);
      }
    };

    fetchGraph();
    return () => {
      active = false;
    };
    // selectedLevelId only filters camera focus on home; graph loads all home series once per season.
  }, [focusLevel, fitGalaxy, homeMapLevels, isHomeMode, levels, selectedSeasonId, isHomeMode ? "" : selectedLevelId]);

  if (loadingSeasons) {
    return <LoadingBlock label="Đang mở BBHD Universe..." />;
  }

  if (!isHomeMode) {
    return (
      <div className="universe-page universe-simulator-page">
        <section className="universe-simulator-hero">
          <div>
            <p className="client-kicker">What-if Simulator</p>
            <h1>Dự đoán kèo đấu giữa hai đội</h1>
            <p>Chọn mùa, serie và hai đội để xem xác suất thắng, sức mạnh tương quan, đối đầu trực tiếp và nhóm VĐV có thể tạo khác biệt.</p>
          </div>
          <div className="universe-control-panel">
            <Form.Group>
              <Form.Label>Mùa</Form.Label>
              <Form.Select value={selectedSeasonId} onChange={(event) => setSelectedSeasonId(event.target.value)}>
                {seasons.map((season) => (
                  <option value={season.id} key={season.id}>
                    {season.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group>
              <Form.Label>Serie</Form.Label>
              <Form.Select value={selectedLevelId} onChange={(event) => setSelectedLevelId(event.target.value)}>
                {levels.map((level) => (
                  <option value={level.id} key={level.id}>
                    {level.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </div>
        </section>

        {error && (
          <Alert variant="danger" className="universe-alert">
            {error}
          </Alert>
        )}

        {loadingGraph ? (
          <LoadingBlock label="Đang tải dữ liệu đội..." />
        ) : (
          <section className="universe-simulator-only-layout">
            <PredictionPanel seasonId={selectedSeasonId} teams={teams} />
            <div className="universe-team-snapshot">
              <div className="universe-panel-title">
                <span>Dữ liệu đang dùng</span>
                <h2>{teams.length} đội trong serie</h2>
              </div>
              <div className="universe-team-snapshot-list">
                {teams.slice(0, 8).map((team) => (
                  <Link to={`/teams/${team.id}`} key={team.id}>
                    <strong>{team.name}</strong>
                    <span>{formatNumber(team.points)} điểm · {team.wins || 0}-{team.losses || 0} · {team.player_count} VĐV</span>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </div>
    );
  }

  return (
    <div className={`universe-page ${isHomeMode ? "universe-page-home" : ""}`}>
      {isHomeMode && <HomeConstellationNav />}
      {!isHomeMode && (
        <section className="universe-hero">
          <div>
            <p className="client-kicker">BBHD Universe</p>
            <h1>Dải ngân hà BBHD: mỗi đội là một hệ sao</h1>
            <p>
              Đội mạnh sáng như lõi hệ sao, VĐV xoay quanh đội theo mùa, đường orbit nối các đội đã đối đầu.
              Màu xanh là điểm mùa dương, màu cam là điểm mùa âm.
            </p>
          </div>
          <div className="universe-control-panel">
            <Form.Group>
              <Form.Label>Mùa</Form.Label>
              <Form.Select value={selectedSeasonId} onChange={(event) => setSelectedSeasonId(event.target.value)}>
                {seasons.map((season) => (
                  <option value={season.id} key={season.id}>
                    {season.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group>
              <Form.Label>Serie</Form.Label>
              <Form.Select value={selectedLevelId} onChange={(event) => setSelectedLevelId(event.target.value)}>
                {levels.map((level) => (
                  <option value={level.id} key={level.id}>
                    {level.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </div>
        </section>
      )}

      {error && (
        <Alert variant="danger" className="universe-alert">
          {error}
        </Alert>
      )}

      <section className="universe-layout">
        <div className="universe-map-shell" ref={wrapRef}>
          {!isHomeMode && <div className="universe-starfield" aria-hidden="true" />}
          {isHomeMode && homeHintVisible && (
            <div className="universe-home-hint">
              <span>
                <b>Chạm đội</b> = zoom hệ sao · <b>Hồ sơ</b> = bảng tin · <b>Nền / Toàn cảnh</b> = về dải ngân hà
              </span>
              <button type="button" aria-label="Đóng gợi ý" onClick={() => setHomeHintVisible(false)}>
                ×
              </button>
            </div>
          )}
          {isHomeMode && (
            <div className="universe-serie-rail" role="toolbar" aria-label="Nhảy serie">
              <button
                type="button"
                className={!selectedLevelId ? "is-on" : ""}
                onClick={() => {
                  setSelectedLevelId("");
                  clearHomeTeamFocus({ fit: true, duration: 900 });
                }}
              >
                <span>All</span>
                <small>ABC</small>
              </button>
              {homeMapLevels.map((level) => {
                const letter = getLevelLetter(level) || level.name;
                const active = String(selectedLevelId) === String(level.id);
                return (
                  <button
                    type="button"
                    key={level.id}
                    className={active ? "is-on" : ""}
                    onClick={() => {
                      setSelectedLevelId(String(level.id));
                      setFocusedLevelNode(null);
                      focusLevel(level.id, 1400);
                    }}
                  >
                    <span>{letter}</span>
                    <small>{getLevelDisplayName(level)}</small>
                  </button>
                );
              })}
            </div>
          )}
          {isHomeMode && (
            <button
              type="button"
              className={`universe-home-tools-toggle ${homeToolsOpen ? "is-open" : ""}`}
              onClick={() => setHomeToolsOpen((open) => !open)}
              aria-expanded={homeToolsOpen}
              aria-controls="universe-home-tools"
            >
              <span>{homeToolsOpen ? "Đóng" : "Tìm đội"}</span>
              <i aria-hidden="true" />
            </button>
          )}
          <div
            id={isHomeMode ? "universe-home-tools" : undefined}
            className={`universe-map-toolbar ${isHomeMode ? "universe-map-toolbar-home" : ""} ${isHomeMode && homeToolsOpen ? "is-open" : ""}`}
          >
            <div>
              <span>{graph.season_name || selectedSeason?.name}</span>
              {isHomeMode ? (
                <input
                  className="universe-team-search"
                  list="universe-team-search-options"
                  value={teamSearchTerm}
                  onChange={(event) => searchTeam(event.target.value)}
                  placeholder="Tìm đội để zoom..."
                  aria-label="Tìm đội"
                />
              ) : (
                <strong>{graph.nodes.length} node</strong>
              )}
              {isHomeMode && (
                <datalist id="universe-team-search-options">
                  {graph.nodes
                    .filter((node) => node.type === "team")
                    .map((team) => (
                      <option value={team.name} key={team.id} />
                    ))}
                </datalist>
              )}
            </div>
            {isHomeMode && (
              <div className="universe-home-filters">
                <Form.Select value={selectedSeasonId} onChange={(event) => setSelectedSeasonId(event.target.value)} aria-label="Chọn mùa">
                  {seasons.map((season) => (
                    <option value={season.id} key={season.id}>
                      {season.name}
                    </option>
                  ))}
                </Form.Select>
                <Button
                  type="button"
                  className={`universe-schedule-toggle ${showScheduleLinks ? "is-active" : ""}`}
                  onClick={() => setShowScheduleLinks((visible) => !visible)}
                  aria-pressed={showScheduleLinks}
                >
                  Lịch
                </Button>
                <Button
                  type="button"
                  className="universe-schedule-toggle"
                  onClick={() => clearHomeTeamFocus({ fit: true, duration: 900 })}
                >
                  Xem hết
                </Button>
                <Button
                  type="button"
                  className="universe-schedule-toggle"
                  onClick={() => galaxyRef.current?.zoomBy?.(0.72)}
                  aria-label="Zoom gần"
                >
                  +
                </Button>
                <Button
                  type="button"
                  className="universe-schedule-toggle"
                  onClick={() => galaxyRef.current?.zoomBy?.(1.35)}
                  aria-label="Zoom xa"
                >
                  −
                </Button>
                <Button
                  type="button"
                  className="universe-schedule-toggle"
                  onClick={() => {
                    setSelectedLevelId("");
                    clearHomeTeamFocus({ fit: true, duration: 700 });
                  }}
                >
                  Reset
                </Button>
              </div>
            )}
            {!isHomeMode && (
              <div className="universe-camera-actions">
                <Button className="client-secondary-btn" onClick={() => zoomCamera(0.72)}>
                  Zoom gần
                </Button>
                <Button className="client-secondary-btn" onClick={() => fitGalaxy()}>
                  Xem hết
                </Button>
                <Button
                  className="client-secondary-btn"
                  onClick={() =>
                    graphRef.current?.cameraPosition?.({ x: 0, y: 90, z: 420 }, { x: 0, y: 0, z: 0 }, 650)
                  }
                >
                  Reset
                </Button>
              </div>
            )}
            {isHomeMode && (
              <div className="universe-home-legend" aria-label="Chú thích">
                <span><i className="legend-team" />Đội</span>
                <span><i className="legend-up" />+điểm</span>
                <span><i className="legend-down" />−điểm</span>
                <span><i className="legend-upcoming" />Trận tới</span>
              </div>
            )}
          </div>
          {!isHomeMode && <UniverseGuide />}
          {isHomeMode && focusedTeamNode && !selectedNode && (
            <div className="universe-team-focus-action" role="status">
              <span>{focusedTeamNode.name}</span>
              <button type="button" onClick={() => setSelectedNode(focusedTeamNode)}>
                Hồ sơ
              </button>
              <button
                type="button"
                className="is-ghost"
                onClick={() => clearHomeTeamFocus({ fit: true, duration: 850 })}
              >
                Toàn cảnh
              </button>
            </div>
          )}

          {loadingGraph ? (
            <LoadingBlock label="Đang dựng bản đồ..." />
          ) : isHomeMode ? (
            <GalaxyHomeScene
              ref={galaxyRef}
              graph={graph}
              width={size.width}
              height={size.height}
              showScheduleLinks={showScheduleLinks}
              focusSerieId={selectedLevelId || focusedLevelNode?.raw_id || focusedLevelNode?.graph_level_id || ""}
              selectedNodeId={selectedNode?.id || focusedTeamNode?.id || ""}
              focusNodeIds={focusNodeIds}
              onNodeClick={selectNode}
              onBackgroundClick={() => {
                if (!focusedTeamNode && !selectedNode) return;
                clearHomeTeamFocus({ fit: true, duration: 850 });
              }}
            />
          ) : (
            <ForceGraph3D
              ref={graphRef}
              width={size.width}
              height={size.height}
              graphData={graph}
              nodeThreeObject={renderNodeObject}
              nodeLabel={(node) => `${node.type === "level" ? "Serie" : node.type === "team" ? "Đội" : "VĐV"}: ${node.name}`}
              linkLabel={(link) =>
                link.type === "upcoming-match" && showScheduleLinks
                  ? `Vòng ${link.round_id}: ${link.home_team_name} vs ${link.guest_team_name}`
                  : ""
              }
              linkVisibility={(link) => link.type !== "upcoming-match" || showScheduleLinks}
              linkColor={(link) => {
                const focused = isFocusedLink(link);
                if (link.type === "upcoming-match") {
                  return focused ? "rgba(255, 221, 138, 0.9)" : "rgba(255, 221, 138, 0.03)";
                }
                return focused ? "rgba(118, 169, 255, 0.28)" : "rgba(118, 169, 255, 0.015)";
              }}
              linkWidth={(link) => {
                if (link.type === "upcoming-match" && !showScheduleLinks) return 0;
                if (isFocusActive && !isFocusedLink(link)) return 0.04;
                return link.type === "upcoming-match" ? 2.2 : 0.18;
              }}
              linkOpacity={0.54}
              linkDirectionalParticles={(link) => (showScheduleLinks && link.type === "upcoming-match" && isFocusedLink(link) ? 3 : 0)}
              linkDirectionalParticleColor={() => "#ffdd8a"}
              linkDirectionalParticleWidth={(link) => (link.type === "upcoming-match" ? 2.6 : 0)}
              linkDirectionalParticleSpeed={0.006}
              d3AlphaDecay={0.018}
              d3VelocityDecay={0.22}
              cooldownTicks={80}
              enableNodeDrag={false}
              onNodeClick={(node) => {
                selectNode(node);
              }}
              onNodeRightClick={(node) => {
                selectNode(node);
              }}
              onBackgroundClick={tuneControls}
              backgroundColor="rgba(3, 7, 18, 0)"
            />
          )}
        </div>

        <aside className={`universe-side-panel ${selectedNode ? "is-open" : ""}`}>
          <NodeDetails
            node={selectedNode}
            onFocusNode={() => focusNode(selectedNode)}
            onClose={
              isHomeMode
                ? () => {
                    // Chi dong bang tin; giu zoom he sao neu dang focus doi.
                    setSelectedNode(null);
                  }
                : undefined
            }
          />
        </aside>
      </section>
    </div>
  );
};

export default Universe;
